import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  #hasUserToggled = false;
  #initialized = false;

  connectedCallback() {
    const { signal } = this.#controller;

    if (!this.#initialized) {
      this.#setDefaultOpenState();
      this.#initialized = true;
    }

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#preserveScrollOnToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    this.#hasUserToggled = true;
    this.#scrollYBeforeToggle = window.scrollY;
  };

  #scrollYBeforeToggle = 0;

  /**
   * Voorkomt dat openen/sluiten van accordion de pagina omhoog springt
   * (vooral bij sticky product-details).
   */
  #preserveScrollOnToggle = () => {
    const scrollY = this.#scrollYBeforeToggle || window.scrollY;
    const sticky = this.closest('.sticky-content--desktop, .sticky-content');
    const stickyScroll = sticky instanceof HTMLElement ? sticky.scrollTop : null;

    const restore = () => {
      if (Math.abs(window.scrollY - scrollY) > 1) {
        window.scrollTo(0, scrollY);
      }
      if (sticky instanceof HTMLElement && stickyScroll != null) {
        sticky.scrollTop = stickyScroll;
      }
    };

    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    // Alleen default open-state zetten als de gebruiker nog niet zelf heeft geklikt
    if (this.#hasUserToggled) return;
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   * Respecteert bestaande HTML `open` bij eerste load; forceert niet opnieuw na user-interactie.
   */
  #setDefaultOpenState() {
    if (this.#hasUserToggled) return;

    const isMobile = isMobileBreakpoint();
    const shouldOpen =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    // Alleen zetten als het anders is — voorkomt onnodige toggle/scroll in theme editor
    if (this.details.open !== shouldOpen) {
      this.details.open = shouldOpen;
    }
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
